import * as z from "zod";

// Ad-click attribution (Meta/Google click ids + UTMs) captured on /signup and
// carried through the Shopify install round-trip alongside the referral token.
const CLICK_ID_MAX_LENGTH = 512;
const UTM_MAX_LENGTH = 200;

export const signupAttributionSchema = z.object({
  fbclid: z.string().min(1).max(CLICK_ID_MAX_LENGTH).optional(),
  gclid: z.string().min(1).max(CLICK_ID_MAX_LENGTH).optional(),
  utmSource: z.string().min(1).max(UTM_MAX_LENGTH).optional(),
  utmMedium: z.string().min(1).max(UTM_MAX_LENGTH).optional(),
  utmCampaign: z.string().min(1).max(UTM_MAX_LENGTH).optional(),
  utmContent: z.string().min(1).max(UTM_MAX_LENGTH).optional(),
  utmTerm: z.string().min(1).max(UTM_MAX_LENGTH).optional(),
  /** Stamped once at capture; reused by every event's buildMetaFbc call. */
  firstObservedAtMs: z.number().int().positive().optional(),
});

export type SignupAttribution = z.infer<typeof signupAttributionSchema>;

type UrlSignupAttributionField = Exclude<
  keyof SignupAttribution,
  "firstObservedAtMs"
>;

const URL_PARAM_FIELDS: [param: string, field: UrlSignupAttributionField][] = [
  ["fbclid", "fbclid"],
  ["gclid", "gclid"],
  ["utm_source", "utmSource"],
  ["utm_medium", "utmMedium"],
  ["utm_campaign", "utmCampaign"],
  ["utm_content", "utmContent"],
  ["utm_term", "utmTerm"],
];

const FIELD_MAX_LENGTH: Record<UrlSignupAttributionField, number> = {
  fbclid: CLICK_ID_MAX_LENGTH,
  gclid: CLICK_ID_MAX_LENGTH,
  utmSource: UTM_MAX_LENGTH,
  utmMedium: UTM_MAX_LENGTH,
  utmCampaign: UTM_MAX_LENGTH,
  utmContent: UTM_MAX_LENGTH,
  utmTerm: UTM_MAX_LENGTH,
};

export function readSignupAttributionFromUrl(
  params: URLSearchParams,
  nowMs: number,
): SignupAttribution | undefined {
  const attribution: SignupAttribution = {};
  for (const [param, field] of URL_PARAM_FIELDS) {
    const value = params.get(param)?.trim().slice(0, FIELD_MAX_LENGTH[field]);
    if (value) {
      attribution[field] = value;
    }
  }
  if (!Object.keys(attribution).length) {
    return undefined;
  }
  attribution.firstObservedAtMs = nowMs;
  return attribution;
}

export function serializeSignupAttribution(
  attribution: SignupAttribution,
): string {
  return JSON.stringify(attribution);
}

/** Tolerant of malformed input (cookie/state corruption): returns undefined. */
export function parseSignupAttribution(
  value: string | null | undefined,
): SignupAttribution | undefined {
  if (!value) {
    return undefined;
  }
  let json: unknown;
  try {
    json = JSON.parse(value);
  } catch {
    return undefined;
  }
  const result = signupAttributionSchema.safeParse(json);
  if (!result.success || !Object.keys(result.data).length) {
    return undefined;
  }
  return result.data;
}

/** Meta's _fbc click-cookie format, synthesized from a URL-borne fbclid. */
export function buildMetaFbc(fbclid: string, nowMs: number): string {
  return `fb.1.${nowMs}.${fbclid}`;
}
